import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  CircularProgress
} from '@mui/material';
import { ArrowBack as BackIcon, History as HistoryIcon, Build as RepairIcon } from '@mui/icons-material';
import QRScanner from '../components/QRScanner';
import { laptopAPI } from '../services/api';
import api from '../services/api';

const QRScanPage = () => {
  const navigate = useNavigate();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [laptop, setLaptop] = useState(null);
  const [history, setHistory] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleScan = async (data) => {
    setError('');
    setLoading(true);
    try {
      const serialNumber = data.serialNumber;
      if (!serialNumber) {
        setError('Invalid QR code data');
        setLoading(false);
        return;
      }

      // Get laptop by serial number first to get the ID
      const response = await laptopAPI.getBySerial(serialNumber);
      if (response.data.success) {
        const laptopData = response.data.data;
        setLaptop(laptopData);

        // Now get full history using the laptop ID
        const fullHistoryResponse = await api.get(`/laptops/${laptopData._id}/full-history`);

        if (fullHistoryResponse.data.success) {
          setHistory(fullHistoryResponse.data.data.changeHistory || []);
          setRepairs(fullHistoryResponse.data.data.repairs || []);
        }
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Laptop not found');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'assigned':
        return 'success';
      case 'unassigned':
        return 'warning';
      case 'in-repair':
        return 'error';
      case 'retired':
        return 'default';
      default:
        return 'default';
    }
  };

  const getRepairStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'info';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            QR Code Scanner
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom align="center">
            Scan Laptop QR Code
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Click the button below to open the camera and scan a laptop's QR code
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => setScannerOpen(true)}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Open Scanner'}
            </Button>
          </Box>

          {laptop && (
            <>
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      {laptop.deviceName}
                    </Typography>
                    <Chip
                      label={laptop.status}
                      color={getStatusColor(laptop.status)}
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Serial Number
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {laptop.serialNumber}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Manufacturer & Model
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {laptop.manufacturer} {laptop.model}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Processor
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {laptop.processor}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Operating System
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {laptop.os}
                      </Typography>
                    </Grid>

                    {laptop.ram && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          RAM
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {laptop.ram}
                        </Typography>
                      </Grid>
                    )}

                    {laptop.storage && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Storage
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {laptop.storage}
                        </Typography>
                      </Grid>
                    )}

                    {laptop.primaryUserEmail && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            Assigned User
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Name
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {laptop.primaryUserDisplayName || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {laptop.primaryUserEmail}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    {laptop.location && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Location
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {laptop.location}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => navigate(`/laptops/${laptop._id}`)}
                    >
                      View Full Details
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => {
                        setLaptop(null);
                        setHistory([]);
                        setRepairs([]);
                        setScannerOpen(true);
                      }}
                    >
                      Scan Another
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Change History Section */}
              {history.length > 0 && (
                <Card sx={{ mt: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <HistoryIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        Configuration & User History ({history.length})
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <List>
                      {history.slice(0, 5).map((record, index) => (
                        <React.Fragment key={record._id}>
                          <ListItem alignItems="flex-start">
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip label={record.changeType} size="small" />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(record.createdAt)}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Changed by: {record.changedByName}
                                  </Typography>
                                  {record.changes.length > 0 ? (
                                    record.changes.map((change, idx) => (
                                      <Typography key={idx} variant="body2">
                                        <strong>{change.field}:</strong> {change.oldValue || 'None'} → {change.newValue}
                                      </Typography>
                                    ))
                                  ) : (
                                    <Typography variant="body2">{record.notes || 'No details'}</Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < Math.min(history.length, 5) - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                    {history.length > 5 && (
                      <Button
                        fullWidth
                        onClick={() => navigate(`/laptops/${laptop._id}`)}
                        sx={{ mt: 2 }}
                      >
                        View All {history.length} Changes
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Repair History Section */}
              {repairs.length > 0 && (
                <Card sx={{ mt: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <RepairIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        Repair History ({repairs.length})
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <List>
                      {repairs.slice(0, 5).map((repair, index) => (
                        <React.Fragment key={repair._id}>
                          <ListItem alignItems="flex-start">
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="subtitle2">
                                    {repair.issueType}
                                  </Typography>
                                  <Chip
                                    label={repair.status}
                                    color={getRepairStatusColor(repair.status)}
                                    size="small"
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(repair.reportedDate)}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body2">
                                    {repair.description}
                                  </Typography>
                                  {repair.cost && (
                                    <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                                      Cost: ${repair.cost.toFixed(2)}
                                    </Typography>
                                  )}
                                  {repair.resolution && (
                                    <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                                      Resolution: {repair.resolution}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < Math.min(repairs.length, 5) - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                    {repairs.length > 5 && (
                      <Button
                        fullWidth
                        onClick={() => navigate(`/laptops/${laptop._id}`)}
                        sx={{ mt: 2 }}
                      >
                        View All {repairs.length} Repairs
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </Paper>
      </Container>

      <QRScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleScan}
      />
    </Box>
  );
};

export default QRScanPage;
